import { useState } from 'react';
import { Badge, Box, Button, FileInput, Group, Stack, Text, useMantineTheme } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconUpload } from '@tabler/icons-react';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';

type BrandingPackFile = {
  fileName: string;
  description: string;
  accept: string;
  isDeployed: boolean;
  isUploading: boolean;
  onUpload: (file: File) => Promise<unknown>;
};

type Props = {
  files: BrandingPackFile[];
  onDownloadPack: () => Promise<void>;
  isDownloadingPack: boolean;
};

function PackFileRow({
  fileName,
  description,
  accept,
  isDeployed,
  isUploading,
  onUpload,
}: Props['files'][number]) {
  const [file, setFile] = useState<File | null>(null);
  const theme = useMantineTheme();

  const handleUpload = async () => {
    if (!file) return;
    try {
      await onUpload(file);
      setFile(null);
      notifications.show({
        title: 'Uploaded',
        message: `${fileName} has been uploaded.`,
        color: 'green',
      });
    } catch (error) {
      notifyAndLogError('Upload failed', `Failed to upload ${fileName}.`, error);
    }
  };

  return (
    <Box p="md" style={{ borderRadius: 16, border: `1px solid ${theme.colors.gray[2]}` }}>
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Text fw={FONT_WEIGHTS.semibold}>{fileName}</Text>
          <Badge variant="light" color={isDeployed ? 'green' : 'gray'}>
            {isDeployed ? 'Deployed' : 'Not uploaded'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'normal' }}>
          {description}
        </Text>
        <Group align="flex-end" gap="sm">
          <FileInput
            placeholder={`Select ${fileName}`}
            accept={accept}
            value={file}
            onChange={setFile}
            flex={1}
            maw={400}
          />
          <Button
            onClick={handleUpload}
            loading={isUploading}
            disabled={!file}
            variant="light"
            leftSection={<IconUpload size={16} />}
          >
            Upload
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}

export default function BrandingPackSection({ files, onDownloadPack, isDownloadingPack }: Props) {
  const hasAnyFile = files.some(packFile => packFile.isDeployed);

  const handleDownloadPack = async () => {
    try {
      await onDownloadPack();
    } catch (error) {
      notifyAndLogError(
        'Download failed',
        'Failed to download the branding pack. It may not exist yet for this tenant.',
        error,
      );
    }
  };

  return (
    <Stack gap="md">
      {files.map(packFile => (
        <PackFileRow key={packFile.fileName} {...packFile} />
      ))}
      <Button
        onClick={handleDownloadPack}
        loading={isDownloadingPack}
        disabled={!hasAnyFile}
        leftSection={<IconDownload size={16} />}
        style={{ alignSelf: 'flex-start' }}
      >
        Download pack
      </Button>
    </Stack>
  );
}
